import * as React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { I18N_NS } from "Config/i18n";
import { ScrollableTable, TableColumnProps } from "Components/ScrollableTable/ScrollableTable";
import { FormatData } from "utils/FormatData";
import { Row, Col } from "antd";
import { documentsTableStore } from "Pages/Project/Documents/Stores/DocumentsTableStore";
import { onlineViewerStore } from "Stores/OnlineViewerStore";
import { wopiViewerStore } from "Stores/WOPIViewerStore";
import { pdfViewerStore } from "Stores/PdfViewerStore";
import { SharedAttachmentsTableItemActions } from "./SharedAttachmentsTableItemActions";
import { SharedAttachmentsTableActions } from "./SharedAttachmentsTableActions";
import { ModalConsumer } from "Components/General/Modal/ModalContext";
import { Button } from "Components/General/Button";
import { AttachProjectFiles } from "./AttachProjectFiles";
import { WithModal } from "Components/General/Modal/withModal";
import { BCFViewerModal } from "Components/BcfViewer/BCFViewerModal";
import { modalProps } from "Pages/Project/QA/utils/modalProps";

const FilenameCell = ({ file }: { file }) => {
  return <span>{file.name}</span>;
};

interface SharedAttachmentsTableProps extends WithTranslation, WithModal {
  data: API_SharedAttachments;
  loading?: boolean;
  removeFiles: (fileIds: number[]) => Promise<void>;
  addFiles: (fileIds: API_FileAttachment[]) => Promise<void>;
  editMode: boolean;
}

interface SharedAttachmentsTableState {
  selectedFiles: API_FileAttachment[];
  selectedFileKeys: string[];
  data: API_SharedAttachments;
}

/**
 * generic project file attachments (based on non generic version from tasks module)
 */
export const SharedAttachmentsTable = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<SharedAttachmentsTableProps, SharedAttachmentsTableState> {
      constructor(props: SharedAttachmentsTableProps) {
        super(props);
        this.state = {
          data: props.data,
          selectedFiles: [],
          selectedFileKeys: [],
        };
      }

      componentDidUpdate() {
        if (this.props.data !== this.state.data) {
          this.setState({
            data: this.props.data,
            selectedFiles: [],
            selectedFileKeys: [],
          });
        }
      }

      resetSelection = () => this.setState({ selectedFiles: [] });

      removeSelectedFiles = () => {
        const { selectedFileKeys, data } = this.state;
        const ids = selectedFileKeys
          .filter((uid) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (!isNaN(uid as any)) {
              return `@id${uid}`;
            }
            return false;
          })
          .map((key) => +key);

        this.props.removeFiles(ids).then(() => {
          data.items = data.items.filter((attachment) => ids.indexOf(attachment.id) === -1);
          this.setState({ selectedFileKeys: [], selectedFiles: [], data: data });
        });
      };

      removeSingleFile = (fileId) => {
        const { selectedFileKeys, selectedFiles, data } = this.state;

        this.props.removeFiles([fileId]).then(() => {
          const fileIndex = selectedFileKeys.findIndex((item) => item === fileId);
          data.items = data.items.filter((attachment) => fileId !== attachment.id);
          if (fileIndex !== -1) {
            selectedFileKeys.splice(fileIndex, 1);
            selectedFiles.splice(fileIndex, 1);
            this.setState({ selectedFileKeys: [], selectedFiles: [], data: data });
          } else {
            this.setState({ data: data });
          }
        });
      };

      handleSelectRows = ({ selectedRows, selectedRowKeys }) => {
        this.setState({ selectedFileKeys: selectedRowKeys, selectedFiles: selectedRows });
      };

      renderFileType = ({ rowData: file }: { rowData: API_FileAttachment }) => {
        // consider that if file was added from project - it would always have "parentId" prop
        if (!(file as API_FileDataEntity).parentId) {
          return file.type;
        } else {
          // consider that extension is in the last part after dot
          const splittedFileName = file.name.split(".");
          if (splittedFileName.length > 1) {
            // if filename has extension - length would be > 1
            return splittedFileName[splittedFileName.length - 1];
          } else {
            return "";
          }
        }
      };

      downloadSelectedFiles = () => {
        if (!this.state.selectedFileKeys.length) {
          return;
        }
        documentsTableStore.downloadFiles(this.state.selectedFiles);
      };

      onSingleActionSelect = (key: string, data: API_FileAttachment) => {
        const { t, showModal } = this.props;

        switch (key) {
          case "download":
            documentsTableStore.downloadFiles([+data.id]);
            break;
          case "bcf_view":
            showModal(BCFViewerModal, {
              modalProps: {
                ...modalProps,
                maskClosable: true,
                width: "100%",
                title: `${t("File")}: ${data.name}`,
                style: { maxWidth: "1300px" },
                className: "full-height-modal with-drawer bcf-modal",
                zIndex: 1013,
              },
              componentProps: { fileId: +data.id },
            });
            break;
          case "easy_view":
            pdfViewerStore.fetch(+data.id, {
              fileName: data.name,
              canDownload: data.fileActions.download || data.fileActions.downloadWithWatermark,
            });
            break;
          case "wopi_view":
            wopiViewerStore.fetch(+data.id, { fileName: data.name, edit: data.viewers.office365Edit });
            break;
          case "online_view":
            onlineViewerStore.fetch(+data.id, { fileName: data.name });
            break;
          case "remove":
            this.removeSingleFile(data.id);
            break;
          default:
            break;
        }
      };

      get tableActions() {
        const config = {
          files: this.state.selectedFiles,
          onDownload: () => this.downloadSelectedFiles(),
          disabledDownload: this.selectedFilesCanNotBeDownloaded,
          onRemoveSelectedFiles: this.props.editMode ? this.removeSelectedFiles : null,
        };
        return config;
      }

      get selectedFilesCanNotBeDownloaded() {
        return this.state.selectedFiles.some((file) => !file.fileActions || !file.fileActions.download);
      }

      addFiles = (files: API_FileAttachment[]): Promise<void> => {
        return this.props.addFiles(files).then(() => {
          const data = this.state.data;
          const items = [...data.items];
          files.forEach((file) => {
            if (!data.items.find((allreadyFile) => allreadyFile.id === file.id)) {
              items.push(file);
            }
          });
          data.items = items;
          this.setState({ data: this.state.data });
        });
      };

      removeFiles = (fileIds: number[]): Promise<void> => {
        return this.props.removeFiles(fileIds);
      };

      render() {
        const { t, data, editMode } = this.props;
        const columns: TableColumnProps<API_FileAttachment>[] = [
          // { dataKey: 'uid', label: t('uid'), width: 100 },
          {
            dataKey: "name",
            label: t("Filename"),
            flexGrow: 1,
            width: 140,
            cellRenderer: ({ rowData }) => <FilenameCell file={rowData} />,
          },
          {
            dataKey: "type",
            label: t("Type"),
            width: 70,
            cellRenderer: this.renderFileType,
          },
          {
            dataKey: "size",
            label: t("Size"),
            width: 80,
            cellRenderer: ({ cellData }) => (!isNaN(cellData) ? FormatData.formatBytes(cellData, 1) : null),
          },
          {
            dataKey: "id",
            label: t("Actions"),
            disableSort: true,
            width: 80,
            cellRenderer: ({ rowData }) => (
              <SharedAttachmentsTableItemActions
                editMode={editMode}
                onRemove={editMode ? this.removeSingleFile : null}
                file={rowData}
              />
            ),
          },
        ];

        return (
          <div className="flex-column" style={{ minHeight: 490 }}>
            {data.notAccessibleCount > 0 && (
              <Row justify="center">
                <Col>
                  {t("For {{y}} project files no access, so these are not listed", {
                    y: data.notAccessibleCount,
                  })}
                </Col>
              </Row>
            )}
            <Row justify="space-between">
              <SharedAttachmentsTableActions {...this.tableActions} />
              <Col>
                <ModalConsumer>
                  {({ showModal }) => (
                    <Button
                      disabled={!editMode}
                      type="primary"
                      faIcon="plus"
                      onClick={() =>
                        showModal(AttachProjectFiles, {
                          modalProps: {
                            title: t("Attach files from project"),
                            width: 800,
                            footer: null,
                          },
                          componentProps: {
                            addFiles: this.addFiles,
                            // removeFiles: this.removeFiles,
                            data: this.props.data?.items,
                          },
                        })
                      }
                    >
                      {t("Add project files")}
                    </Button>
                  )}
                </ModalConsumer>
              </Col>
            </Row>
            <ScrollableTable<API_FileAttachment>
              //stretchHeight={true}
              wrapperStyles={{ height: null, flex: "1 1 auto", minHeight: 270 }}
              rowKey="id"
              data={data.items}
              columns={columns}
              onSelectRows={this.handleSelectRows}
              selectedRowKeys={this.state.selectedFileKeys}
            />
          </div>
        );
      }
    }
  )
);
